import { browserHistory } from 'react-router';
import config from 'config/global';
import * as localStorageUtil from 'utils/localStorage';
import LHX from 'utils/LHX';
import { PAYMENT_PLAN_TYPES } from 'constants/payment';

const BOARD_BASIS_LABEL_MAP = {
  AI: 'All Inclusive',
  BB: 'Bed & Breakfast',
  FB: 'Full Board',
  HB: 'Half Board',
  RO: 'Room Only',
  SC: 'Self Catering',
};

export const BOARD_BASIS_CODE_MAP = {
  38: 'AI',
  39: 'FB',
  41: 'BB',
  40: 'HB',
  42: 'SC',
  43: 'RO',
};

const BOARD_BASIS_DIMVAL_MAP = {
  AI: 38,
  FB: 39,
  BB: 41,
  HB: 40,
  SC: 42,
  RO: 43,
};

const FLEXIBLE_DURATION_LABELS = {
  1205: '3 - 5',
  1206: '6 - 8',
  1207: '9 - 11',
  1209: '13 - 15',
};

const PAYMENT_PLAN_LABELS = {
  [PAYMENT_PLAN_TYPES.SPREAD_THE_COST]: 'Monthly Payments',
  [PAYMENT_PLAN_TYPES.LOW_DEPOSIT]: 'Low Deposit',
  [PAYMENT_PLAN_TYPES.STANDARD_DEPOSIT]: 'Standard Deposit',
  [PAYMENT_PLAN_TYPES.PAY_IN_FULL]: 'Pay Full Amount',
};

export function setCookie(cookieName, value, expiryDate) {
  const settings = [
    `${cookieName}=${value}`,
    `expires=${expiryDate ? expiryDate.toUTCString() : ''}`,
    'path=/',
  ];
  window.document.cookie = settings.join('; ');
}

export function setSessionCookie(cookieName, value) {
  const settings = [
    `${cookieName}=${value}`,
    'path=/',
  ];
  window.document.cookie = settings.join('; ');
}

export function readCookie(key) {
  const cookieItems = window.document.cookie.split('; ');
  let value;

  cookieItems.some((item) => {
    const equalsIndex = item.indexOf('=');
    const cookieKeyValue = item.substring(0, equalsIndex);

    if (cookieKeyValue === key) {
      value = item.substring(equalsIndex + 1);
    }

    return !!value;
  });

  return value;
}

export function isDomainIe() {
  const domain = '.ie';
  return window.document.domain.substr(-(domain.length)) === domain || LHX.isEnabled('domainIe');
}

export function isCurrencyEur() {
  return isDomainIe();
}

export function getBoardBasisDimVal(boardBasisCode) {
  return BOARD_BASIS_DIMVAL_MAP[boardBasisCode];
}

export function getBoardBasisLabel(boardBasisCode) {
  return BOARD_BASIS_LABEL_MAP[boardBasisCode];
}

export function getBoardBasisCode(boardBasisDimId) {
  return BOARD_BASIS_CODE_MAP[boardBasisDimId];
}

export function getFlexDurationNights(durationDimId) {
  return FLEXIBLE_DURATION_LABELS[durationDimId];
}

export function getPaymentPlanLabel(type) {
  return PAYMENT_PLAN_LABELS[type];
}

export function getBoardBasisLowToHigh() {
  return ['RO', 'SC', 'BB', 'HB', 'FB', 'AI'];
}

export function getSelectedDurationDays(baseState, dimensionsEntities) {
  let days = '';
  const dimId = baseState.getIn(['D_Nights', 0]);
  if (dimId) {
    const isFlexDurationSelected = baseState.getIn(['queryState', 'props', 'nightFlex', 'set']);
    days = isFlexDurationSelected ?
      getFlexDurationNights(dimId) : dimensionsEntities.getIn([`${dimId}`, 'label']);
  }
  return days;
}

export const dayInMilliseconds = 1000 * 60 * 60 * 24;

export function titleCase(text) {
  return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase();
}

export function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.substr(1);
}

/* istanbul ignore next */
export function updatePageTitle(title) {
  const pageTitleSuffix = isDomainIe() ? '| loveholidays.ie' : '| loveholidays.com';

  window.document.head.querySelector('title').innerText = `${title} ${pageTitleSuffix}`;
}

// make sure we are consistently rounding amounts on the client
export function round(amount) {
  return Math.ceil(amount);
}

export function formatPrice(amount, decimalPlaces = 0, addCurrencySymbol = true) {
  let output = amount;

  if (typeof amount === 'string') {
    output = parseFloat(amount);
  }

  if (!decimalPlaces) {
    output = round(output);
  } else {
    output = output.toFixed(decimalPlaces);
  }

  if (addCurrencySymbol) {
    output = isCurrencyEur() ? `€${output}` : `£${output}`;
  } else {
    output = `${output}`;
  }

  return output;
}

export function penceToPounds(pence) {
  return parseInt(pence, 10) / 100;
}

export function formatPenceToPounds(pence, decimalPlaces = 0, addCurrencySymbol = true) {
  return formatPrice(penceToPounds(pence), decimalPlaces, addCurrencySymbol);
}

export function percentToNumber(percent) {
  return percent * 0.01;
}

/* istanbul ignore next */
export function getPreviousSiblings(elem) {
  const siblings = [];
  while (elem) {
    elem = elem.previousElementSibling;
    siblings.push(elem);
  }
  return siblings;
}

// objectPath arg is String 'rootObj.level2.level3.level4.etc.etc'
export function checkForNestedProperty(objectToTest, objectPath) {
  const pathLevels = objectPath.split('.');
  let obj = objectToTest;

  for (let i = 0; i < pathLevels.length; i++) {
    if (!obj || !obj.hasOwnProperty(pathLevels[i])) {
      return false;
    }
    obj = obj[pathLevels[i]];
  }

  return true;
}

/* istanbul ignore next */
export function guid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

export function serialize(obj) {
  const keyValuePairsArray = [];

  for (const k in obj) {
    if (obj.hasOwnProperty(k)) {
      keyValuePairsArray.push(`${k}=${obj[k]}`);
    }
  }

  return keyValuePairsArray.join('&');
}

export function isSubPage(locationObj) {
  return locationObj.pathname.indexOf('/sub-page') !== -1;
}

export function getSubPageURL(subPath, locationObj) {
  const newPath = locationObj.pathname
    .replace(/\.html.*/, '')
    .replace(/\/$/, '')
    .replace(/\/sub-page.*/, '');

  return `${newPath}/sub-page/${subPath}${locationObj.search}${locationObj.hash || ''}`;
}

export function getSubPageLocation(subPath, locationObj) {
  const newPath = locationObj.pathname
    .replace(/\.html.*/, '')
    .replace(/\/$/, '')
    .replace(/\/sub-page.*/, '');

  return Object.assign({}, locationObj, {
    pathname: `${newPath}/sub-page/${subPath}`,
  });
}

export function getChildPageURL(childPath, locationObj) {
  let subPath;
  const path = locationObj.pathname;
  const newPath = locationObj.pathname
    .replace(/\.html.*/, '')
    .replace(/\/$/, '')
    .replace(/\/sub-page.*/, '');

  if (path.indexOf('sub-page') > -1) {
    const subParams = locationObj.pathname
      .match(/sub-page\/(.*)/)
      .pop();
    subPath = `${subParams}/`;
  } else {
    subPath = '';
  }

  return `${newPath}/sub-page/${subPath}${childPath}${locationObj.search}${locationObj.hash || ''}`;
}

export function getParentRoute(locationObj) {
  const match = locationObj.pathname.match(/\/([^\/]*)\/[^\/]*$/);
  const parentRoute = match ? match[1] : '';
  return parentRoute && parentRoute !== 'sub-page' ? `${parentRoute}/` : '';
}

export function getParentPathname(locationObj) {
  const hasDotHTMLSuffix = !locationObj.pathname.match(/^\/holidays\/sub-page/);
  let newPath = locationObj.pathname
    .replace(/\.html.*/, '')
    .replace(/\/sub-page.*/, '');
  const suffix = hasDotHTMLSuffix && newPath ? '.html' : '';
  const subParams = locationObj.pathname
    .match(/sub-page\/(.*)/)
    .pop()
    .split('/');
  subParams.pop();
  const subPath = subParams.join('/');

  if (newPath) {
    newPath = `${newPath}${subParams.length ? '/sub-page/' : suffix}${subPath}`;
  } else {
    newPath = '/';
  }

  return newPath;
}

export function getParentRouteURL(locationObj) {
  return `${getParentPathname(locationObj)}${locationObj.search}${locationObj.hash || ''}`;
}

export function getParentRouteLocation(locationObj) {
  return Object.assign({}, locationObj, {
    pathname: getParentPathname(locationObj),
  });
}

export function getURLForAPI(locationObj) {
  const strippedPathname = locationObj.pathname
    .replace('/availability', '')
    .replace(/\.html.*/, '')
    .replace(/\/sub-page.*/, '');

  return `${strippedPathname}.html`;
}

export function getPandaURL(detailLink, state) {
  const link = detailLink ? detailLink.replace(/^\/holidays/, '/holidays/availability') : '';
  return `${link}?alltabs=1${state ? `&state=${state}` : ''}`;
}

export function getDetailsURL(detailLink, state) {
  return `${detailLink}?alltabs=1${state ? `&state=${state}` : ''}`;
}

export function getSelectedDimensionNumberOfNights(props) {
  if (props.baseState.get('D_Nights')) {
    const selectedNights = props.baseState.get('D_Nights').map(dimId =>
      Number(props.dimensionsEntities.getIn([`${dimId}`, 'label']), 10),
    );

    return selectedNights.first();
  }
}

export function getSelectedNumberOfNights(props) {
  return props.appState && props.appState.get('nonDimensionNumberOfNights') || getSelectedDimensionNumberOfNights(props);
}

export function getCheapestOfferOfDay(props) {
  let alternatives = props.calendarOffers.get('alternatives');
  let cheapestOfferOfDay;

  if (!alternatives || (alternatives && !alternatives.size)) {
    alternatives = props.calendarOffers.get('offers');
  }

  if (alternatives && alternatives.size) {
    const selectedNights = getSelectedNumberOfNights(props);
    cheapestOfferOfDay = alternatives.filter(altOffer =>
      selectedNights === altOffer.get('nights'),
    ).first();
  }

  return cheapestOfferOfDay;
}

export function getDurationDimId(numberOfNights) {
  const dimensionMap = {
    1: 2662,
    2: 2663,
    3: 2664,
    4: 1205,
    5: 2665,
    6: 2666,
    7: 1206,
    8: 2667,
    9: 2668,
    10: 1207,
    11: 1208,
    12: 2669,
    13: 2670,
    14: 1209,
    15: 2671,
  };
  return dimensionMap[numberOfNights];
}

export function getDurationFromDimId(dimId) {
  const durationMap = {
    2662: 1,
    2663: 2,
    2664: 3,
    1205: 4,
    2665: 5,
    2666: 6,
    1206: 7,
    2667: 8,
    2668: 9,
    1207: 10,
    1208: 11,
    2669: 12,
    2670: 13,
    1209: 14,
    2671: 15,
  };
  return durationMap[dimId];
}

export function getMinPriceFromAlternatives(alternativeOffers, numberOfNights) {
  const minPrice = alternativeOffers.size && Math.min(...alternativeOffers.filter(alternativeOffer =>
      (alternativeOffer.get('nights') === numberOfNights)).map(alternativeOffer =>
      alternativeOffer.get('discountPrice')).toArray());

  return (minPrice && minPrice !== Infinity) ? minPrice : 'Unavailable';
}

export function calculateDepartureDate(deptDate) {
  const nowDayStamp = Math.floor(Date.now() / dayInMilliseconds);
  return deptDate > nowDayStamp ? deptDate : null;
}

export function isCalOrDeptDateExpired(baseState) {
  return ['calDate', 'deptDate'].some((dateKey) => {
    const date = calculateDepartureDate(baseState.getIn(['queryState', 'props', dateKey, 'value']));
    const selected = baseState.getIn(['queryState', 'props', dateKey, 'selected']);

    return !date && selected;
  });
}

export function getMonthToShowForCalendar(deptDate, today = new Date()) {
  const date = today;
  if (date.getDate() > 15) {
    date.setMonth(date.getMonth() + 1);
  }
  return deptDate || Math.floor(date.getTime() / dayInMilliseconds);
}

/* istanbul ignore next */
export function debounce(func, wait, immediate) {
  let timeout;

  return () => {
    const context = this;
    const args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export function throttle(callback, delay) {
  const sixtyFPS = 1000 / 60;
  const requestAnimationFrame = window.requestAnimationFrame ||
    window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame ||
    function setTimeoutFallback(fn) { window.setTimeout(fn, sixtyFPS); };

  const fn = delay ? window.setTimeout : requestAnimationFrame;
  let ticker;
  return () => {
    const context = this;
    const args = arguments;
    if (!ticker) {
      ticker = fn(() => {
        callback.apply(context, args);
        ticker = null;
      }, delay);
    }
  };
}

export function obj2Str(obj) {
  return Object.keys(obj).map(key => `${key}=${obj[key]}`).join('&');
}

export function obj2Hash(obj) {
  const hashString = obj2Str(obj);
  return `${hashString ? '#' : ''}${hashString}`;
}

export function hash2Obj(hash) {
  return hash && hash.length !== 1 ? Object.assign({}, ...hash.slice(1).split('&').map((prop) => {
    const splitProp = prop.split('=');
    return { [splitProp[0]]: splitProp[1] };
  })) : {};
}

export function isEveryNth(index, elementNumber) {
  return (index + 1) % elementNumber === 0;
}

export function getNumberOfInfants(childrenAgesCollection = []) {
  const maxInfantAge = 1;
  const filteredCollection = childrenAgesCollection.filter(age => age <= maxInfantAge);
  return Array.isArray(filteredCollection) ? filteredCollection.length : filteredCollection.size;
}

export function hasHotelOnlyResultsRequiredParam(locationObj) {
  return locationObj.query.deptDate && locationObj.query.retDate;
}

export function range(start, end, step = 1) {
  /* eslint no-param-reassign: 0 */

  if (!end) {
    end = start;
    start = 0;
  }

  return Array(...Array(end - start))
    .map((value, i) => (i * step) + start);
}

export function buildURL(location, options) {
  const newLocation = Object.assign({}, location, options);
  let hashObj = hash2Obj(newLocation.hash);
  let newQuery = newLocation.query;

  if (options.query) {
    newQuery = Object.assign({}, location.query, options.query);
    Object.keys(options.query).forEach((key) => {
      if (options.query[key] === undefined) {
        delete newQuery[key];
      }
    });
  }

  if (options.deleteHashParams) {
    options.deleteHashParams.map((key) => {
      delete hashObj[key];
    });
  }

  if (options.hashObj) {
    hashObj = Object.assign({}, hashObj, options.hashObj);
  }

  return `${newLocation.pathname}?${serialize(newQuery)}${obj2Hash(hashObj)}`;
}

export function updateWindowLocation(url) {
  window.location.href = url;
}

export function updateURLWithReplace(location, options = {}) {
  const newURL = buildURL(location, options);
  browserHistory.replace(newURL);
}

export function updateURLWithPush(location, options = {}) {
  const newURL = buildURL(location, options);
  browserHistory.push(newURL);
}

export function updateLocationHashWithPush(location, hashObj, replace = false) {
  const newHashObj = replace ? hashObj : Object.assign({}, hash2Obj(location.hash), hashObj);
  const hash = obj2Hash(newHashObj);
  browserHistory.push(`${location.pathname}?${serialize(location.query)}${hash}`);
}

export function updatePaginationHashWithPush(start, location, resultsPerPage = config.paginationDefaults.optionsCount) {
  const pageNum = (start / resultsPerPage) + 1;
  updateLocationHashWithPush(location, {
    page: pageNum,
  });
}

export function updateEncodedStateSearchParam(previousStateEncoded, currentStateEncoded, location) {
  if (currentStateEncoded && currentStateEncoded !== previousStateEncoded) {
    localStorageUtil.setItem(localStorageUtil.KEYS.currentStateEncoded, currentStateEncoded);
    localStorageUtil.setItem(localStorageUtil.KEYS.currentStateEncodedTimestamp, Date.now());

    updateURLWithReplace(location, {
      query: { state: currentStateEncoded },
      hashObj: { state: currentStateEncoded }, // this line can go once panda is completely migrated to react
    });
  }
}

const WEB_TRENDS_EVENT_TYPE_MAP = {
  HO: 'HotelOnly',
  HL: 'PandA',
};

export function getWebTrendsEventType(hash) {
  const viewHashAttr = hash2Obj(hash).view;
  return viewHashAttr ? WEB_TRENDS_EVENT_TYPE_MAP[viewHashAttr] : 'PandA';
}

/* eslint-disable no-console */
export function fireGTMEvent(pageType, masterId) {
  if (window.ignoredFirstGTMCall) {
    window.dataLayer = window.dataLayer || [];
    window.google_tag_params = { hrental_id: masterId || null, hrental_pagetype: pageType };
    const eventPayload = {
      codeBase: 'react',
      event: 'AllPageview',
      masterId,
    };
    window.dataLayer.push(eventPayload);
    /* istanbul ignore if */
    if (__DEBUG__) console.debug('fireGTMEvent', window.google_tag_params, eventPayload);
  } else {
    window.ignoredFirstGTMCall = true;
  }
}

export function trackPageChange(pageType, masterId) {
  fireGTMEvent(pageType, masterId);
}

/* istanbul ignore next */
export function fireWebtrendsPageView(pageType, extraParams = []) {
  if (window && window.dcsMultiTrack) {
    const lvhid = readCookie('lvhid');
    window.dcsMultiTrack('DCS.dcsuri', window.location.pathname, 'WT.ti', pageType, 'WT.dl', '0', 'WT.lvhid', lvhid, ...extraParams);
  }
}

export function fireWebtrendsBookPageView(pageType, extraParams = []) {
  if (window && window.dcsMultiTrack) {
    const lvhid = readCookie('lvhid');
    window.dcsMultiTrack('DCS.dcsuri', window.location.pathname, 'WT.ti', pageType, 'WT.dl', '0', 'WT.lvhid', lvhid, ...extraParams);
  }
}

/* istanbul ignore next */
export function fireWebtrendsEvent(pageType, extraParams = []) {
  if (window && window.dcsMultiTrack) {
    const lvhid = readCookie('lvhid');
    window.dcsMultiTrack('DCS.dcsuri', window.location.pathname, 'WT.ti', pageType, 'WT.dl', '99', 'WT.lvhid', lvhid, ...extraParams);
  }
}

export function addUrlParam(url, key, val) {
  const newParam = `${key}=${val}`;
  let params = `?${newParam}`;

  params = url.replace(new RegExp(`([?&])${key}[^&]*`), `$1${newParam}`);

  if (params === url) {
    params += `${url.includes('?') ? '&' : '?'}${newParam}`;
  }

  return params;
}

export function isIpad() {
  return window.navigator.userAgent.match(/iPad/i) !== null;
}

export function getCoordinatesRelativeToDocument(boundingClientRect) {
  const body = window.document.body;
  const docEl = window.document.documentElement;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const appRoot = document.getElementById('root');
  const semseoPattern = /search-unit/i;

  let left = Math.round(boundingClientRect.left + scrollLeft);
  let top = Math.round(boundingClientRect.top + scrollTop);

  /*
   * Here we assume immediate parent of #root has relative position
   * Unless a semseo page
   */
  if (appRoot && appRoot.parentNode !== body && appRoot.parentNode.className.match(semseoPattern) === null) {
    const appRootBoundingClientRect = appRoot.getBoundingClientRect();

    left = Math.round(boundingClientRect.left - appRootBoundingClientRect.left);
    top = Math.round(boundingClientRect.top - appRootBoundingClientRect.top);
  }

  return { left, top };
}

export function replaceWithTheClosestDefinedNumber(definedNumbersArray, numberToReplace) {
  let smallestDelta = Infinity;
  let roundTo = numberToReplace;
  definedNumbersArray.forEach((number) => {
    const delta = Math.abs(number - numberToReplace);
    if (delta < smallestDelta) {
      smallestDelta = delta;
      roundTo = number;
    }
  });
  return roundTo;
}

export function snakeCaseToHyphenCase(value) {
  return value.replace('_', '-');
}

export function camelCaseToHyphenCase(value) {
  return value.replace(/([A-Z])/g, g => `-${g[0].toLowerCase()}`);
}

export function camelCase(value) {
  return value.toLowerCase().split(' ').map(capitalize).join(' ');
}

export function getDataAttributes(data) {
  const dataAttributes = {};
  const dataKeys = Object.keys(data);
  dataKeys.forEach((key) => {
    dataAttributes[`data-${camelCaseToHyphenCase(key)}`] = data[key];
  });
  return dataAttributes;
}

export function sessionVariableExists(variable) {
  return sessionStorage.getItem(variable);
}

export function setSessionVariable(key, value) {
  sessionStorage.setItem(key, value);
}

/* istanbul ignore next */
export function injectScript(src, { async, onLoad }) {
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.src = src;

  if (async) {
    scriptElement.async = true;
  }

  if (onLoad) {
    scriptElement.onload = onLoad;
  }

  const firstScriptElement = document.getElementsByTagName('script')[0];
  firstScriptElement.parentNode.insertBefore(scriptElement, firstScriptElement);
}

export function isHotelOnly(pathName) {
  return pathName.indexOf('/hotel-only/') !== -1;
}

export function httpOrigin() {
  return window.location.origin.replace('https://', 'http://');
}

export function scrollIfNotVisible(el, parentEl) {
  const { top: elTop, bottom: elBottom } = el.getBoundingClientRect();
  const { top: parentElTop, bottom: parentElBottom } = parentEl.getBoundingClientRect();

  const isVisibleWithinParent = elTop >= parentElTop && elBottom <= parentElBottom;
  const isVisibleWithinWindow = elTop >= 0 && elBottom <= window.innerHeight;

  if (!isVisibleWithinParent || !isVisibleWithinWindow) {
    el.scrollIntoView();
  }
}

export const allDestinationsDimVal = 214;



/** WEBPACK FOOTER **
 ** ./src/utils/utils.js
 **/